import React, { Component } from "react";
import { Route, Switch } from "react-router-dom";
import { graphql, Mutation } from "react-apollo";
import gql from "graphql-tag";
import { Box, Button } from "mineral-ui";
import "./App.css";
import "./new.css";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Link from "./components/Link";
import MaxWidth from "./components/MaxWidth";

// Public pages
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Validate from "./pages/Validate";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";

import { H1, H2, P } from "./components/html";

// Non-public pages
import HomeSansGuard from "./pages/Home";
import SettingsSansGuard from "./pages/Settings";
import ChangePlanSansGuard from "./pages/ChangePlan";

const Simple = ({ children }) => (
  <Box>
    <MaxWidth>
      <Box>{children}</Box>
    </MaxWidth>
  </Box>
);
const Loading = () => <Simple>Loading...</Simple>;
const LoginRequired = () => (
  <Simple>
    <H1>Login required</H1>
    <P>
      <Link to="/login">Click here to log in</Link> or{" "}
      <Link to="/">here to return home</Link>.
    </P>
  </Simple>
);

class AgreeToTerms extends Component {
  state = {
    agreed: false,
  };

  render() {
    return (
      <Simple>
        <Box
          style={{ maxWidth: "28em" }}
          marginTop="6em"
          marginHorizontal="auto"
        >
          <H2>Welcome to the Graphile Store</H2>
          <P>
            Before you can continue, you must agree to our{" "}
            <Link href="/terms" target="_blank">
              Terms and Conditions of Use
            </Link>{" "}
            and{" "}
            <Link href="/privacy" target="_blank">
              Privacy and Cookie Policy
            </Link>
            .
          </P>
          <P>
            Please take a few minutes to read these now. When you're done, if
            and only if you agree to the terms please complete the form below.
            If you do not agree, please cease using this site.
          </P>
          <Mutation
            mutation={gql`
              mutation AgreeToTerms {
                agreeToTerms(input: {}) {
                  user {
                    nodeId
                    uuid
                    uuidShort
                    hasAgreedToTerms
                  }
                }
              }
            `}
          >
            {agreeToTerms => (
              <form
                onSubmit={e => {
                  e.preventDefault();
                  if (this.state.agreed) {
                    agreeToTerms();
                  }
                }}
              >
                <p>
                  <label htmlFor="agreeCheckbox">
                    <input
                      id="agreeCheckbox"
                      type="checkbox"
                      checked={this.state.agreed}
                      onChange={e =>
                        this.setState({ agreed: e.target.checked })
                      }
                    />{" "}
                    I have read, understood and agree to be legally bound by the
                    Terms and Conditions of Use and the Privacy and Cookie
                    Policy for this website.
                  </label>
                </p>
                <Button primary disabled={!this.state.agreed} type="submit">
                  Agree
                </Button>
              </form>
            )}
          </Mutation>
        </Box>
      </Simple>
    );
  }
}

const Guard = (GuardedComponent, allowLoggedOut = false) => args => {
  const {
    data: { viewer, loading, error },
  } = args;
  if (!viewer) {
    if (error) {
      return <Simple>An error occurred: {error.message}</Simple>;
    } else if (loading) {
      return <Loading />;
    } else if (allowLoggedOut) {
      return <GuardedComponent {...args} />;
    } else {
      return <LoginRequired {...args} />;
    }
  } else if (viewer.hasAgreedToTerms) {
    return <GuardedComponent {...args} />;
  } else {
    return <AgreeToTerms {...args} />;
  }
};

const Home = Guard(HomeSansGuard, true);
const Settings = Guard(SettingsSansGuard);
const ChangePlan = Guard(ChangePlanSansGuard);

class App extends Component {
  state = {};
  render() {
    const { data } = this.props;
    return (
      <div>
        <Header data={data} />
        <Box
          element="section"
          marginVertical="md"
          style={{ minHeight: "calc(100vh - 12rem)" }}
        >
          <MaxWidth>
            <Switch>
              <Route exact path="/" render={() => <Home data={data} />} />
              <Route
                exact
                path="/validate"
                render={routeProps => <Validate {...routeProps} data={data} />}
              />
              <Route
                exact
                path="/settings"
                render={() => <Settings data={data} />}
              />
              <Route
                exact
                path="/settings/change-plan"
                render={props => <ChangePlan data={data} {...props} />}
              />
              <Route exact path="/login" render={() => <Login data={data} />} />
              <Route exact path="/terms" render={() => <Terms data={data} />} />
              <Route
                exact
                path="/privacy"
                render={() => <Privacy data={data} />}
              />
              <Route render={() => <NotFound data={data} />} />
            </Switch>
          </MaxWidth>
        </Box>
        <Footer data={data} />
      </div>
    );
  }
}

export default graphql(gql`
  query PlansQuery {
    viewer {
      nodeId
      uuid
      uuidShort
      firstName
      lastName
      lastLoginAt
      hasAgreedToTerms
    }
    allPlans(first: 10) {
      nodes {
        nodeId
        id
        slug
        name
        currency
        source
        level
        planInfo {
          amount
          currency
          interval
          intervalCount
        }
        viewerHasPlan
        viewerPlanExpires
      }
    }
    allRepos(first: 10) {
      nodes {
        nodeId
        id
        slug
        moduleName
        name
        description
        viewerStatus
        planRepos: planReposByRepoId(first: 50) {
          nodes {
            nodeId
            plan: planByPlanId {
              nodeId
              id
              slug
              name
              currency
              source
              level
              planInfo {
                amount
                currency
                interval
                intervalCount
              }
              viewerHasPlan
              viewerPlanExpires
            }
          }
        }
      }
    }
  }
`)(App);
